// The Submit hero (DESIGN_SPEC §11.2, components §13; RESTYLE §2): Conifer's lockup, CTA pair and specimen plate.
//   <SubmitHero left specimen />      section.hero: two equal columns; under 900px Conifer's stack (lockup → CTAs → hint →
//                                     plate → run sheet) via the .hero orders in components.css
//   <Lockup />
//   <CtaBlock … />                    the gold CTA is the only gold on the page:
//                                     file: "Choose a sample… →" + secondary "Paste a URL" · hashing: "Hashing… 42%" ·
//                                     picked: "Submit <file> →  Ctrl Enter" · URL mode: URL field + "Submit URL →" + text "Cancel" ·
//                                     sign-in required: a plain <a> "Sign in with GitHub to submit →" (return=/, with the
//                                     typed URL). The Cloudflare check runs on its own: a submit made before its token
//                                     arrives waits for it (submitting covers that wait). A batch (several files, or an
//                                     archive) reads "Submit 3 files →" / "Submit <archive> →".
//   <PickedLine … /> · <SeenLine seen /> (the filled verdict banner of the newest match)
//   <BatchLine batch onRemove onAdd onClear />   the batch: each file (name · archive · size · ×), then n/5, what was
//                                                left out or skipped, and add / clear
import { useId, type ReactNode, type Ref } from "react";
import { Link } from "react-router-dom";
import { Arrow, Button, buttonClass, Field, Hash, Icon, Input, Kbd, Mark, MicroTag, MT } from "@/components/ui";
import { useClock } from "@/hooks";
import { ago, bytes } from "@/lib/format";
import { pastTimeout } from "@/lib/records";
import type { Job } from "@/types";
import { isArchiveName, MAX_BULK, type Batch } from "./batch";
import { needsStream, recordStatus, RunMark, StreamedStatus } from "./RunMark";
import type { PickState } from "./usePickFile";

export function SubmitHero({ left, specimen }: { left: ReactNode; specimen: ReactNode }) {
  return (
    <section className="hero" aria-label="Submit a sample">
      <div className="hero-left">{left}</div>
      {specimen}
    </section>
  );
}

export function Lockup() {
  return (
    <h1 className="lockup">
      <Mark className="lockup-mark" />
      <span className="lockup-rule" aria-hidden="true" />
      <span className="lockup-word">malbox</span>
    </h1>
  );
}

type CtaProps = {
  mode: "file" | "url";
  pick: PickState;
  /** A submit is actually in flight (the fetch). */
  submitting: boolean;
  /**
   * A click fired but the request is parked waiting for Turnstile's token
   * to arrive — rendered as "Checking…" so the viewer sees it's waiting on
   * a browser check, not stuck mid-POST.
   */
  waitingForCheck?: boolean;
  /**
   * Turnstile is showing an interactive challenge the viewer hasn't solved
   * yet. Submit stays visibly disabled (not just an inert spinner) so the
   * UI doesn't read as a bug — the viewer can see WHY nothing's happening.
   */
  blocked?: boolean;
  /** Several files (or one archive) going to POST /v1/jobs/bulk instead of the picked sample. */
  batch?: Batch | null;
  signIn: string | null;
  url: string;
  urlError: string | null;
  urlRef: Ref<HTMLInputElement>;
  onUrl: (v: string) => void;
  onChoose: () => void;
  onPasteUrl: () => void;
  onCancelUrl: () => void;
  onSubmit: () => void;
};

const CHECK_HINT = "Waiting for the Cloudflare check — solve it if a widget appears, otherwise it validates on its own";

function Submitting() {
  return (
    <>
      <span className="breathe">Submitting</span>…
    </>
  );
}

function Checking() {
  return (
    <>
      <span className="breathe">Checking</span>…
    </>
  );
}

export function CtaBlock(p: CtaProps) {
  const id = useId();
  if (p.signIn) {
    return (
      <div className="ctas">
        <a className={buttonClass({ variant: "cta", size: "lg" })} href={p.signIn}>
          <Icon name="github" size={14} />
          Sign in with GitHub to submit
          <Arrow />
        </a>
      </div>
    );
  }

  if (p.mode === "url") {
    const inert = p.submitting || p.blocked;
    return (
      <form
        className="ctas w-[min(480px,100%)] flex-col items-stretch text-left"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (!inert) p.onSubmit();
        }}
      >
        <Field label="URL" htmlFor={`${id}-url`} error={p.urlError} messageId={`${id}-url-m`}>
          <Input
            ref={p.urlRef}
            id={`${id}-url`}
            type="url"
            inputMode="url"
            autoFocus
            autoComplete="off"
            translate="no"
            placeholder="https://example.test/landing"
            value={p.url}
            invalid={!!p.urlError}
            aria-describedby={p.urlError ? `${id}-url-m` : undefined}
            onChange={(e) => p.onUrl(e.target.value)}
          />
        </Field>
        <div className="flex flex-wrap justify-center gap-3 max-[720px]:flex-col">
          <Button
            type="submit"
            variant="cta"
            size="lg"
            arrow={!inert}
            aria-disabled={inert || undefined}
            title={p.blocked ? CHECK_HINT : undefined}
          >
            {p.waitingForCheck ? <Checking /> : p.submitting ? <Submitting /> : "Submit URL"}
          </Button>
          <Button variant="ghost" size="lg" onClick={p.onCancelUrl}>
            Cancel
          </Button>
        </div>
      </form>
    );
  }

  const s = p.pick;
  const n = p.batch?.files.length ?? 0;
  const ready = p.batch ? (n ? (n === 1 ? `Submit ${p.batch.files[0].name}` : `Submit ${n} files`) : null) : s.status === "picked" ? `Submit ${s.file.name}` : null;
  const inert = p.submitting || p.blocked || p.waitingForCheck;
  const busy = p.submitting || p.waitingForCheck;
  let primary: ReactNode;
  if (ready) {
    // the key hint is for keyboards: it drops out on touch screens and narrow ones, where the file name needs the room
    primary = (
      <button
        type="button"
        className={buttonClass({ variant: "cta", size: "lg", className: "max-w-full" })}
        aria-disabled={inert || undefined}
        aria-keyshortcuts="Control+Enter"
        title={p.blocked ? CHECK_HINT : ready}
        onClick={() => !inert && p.onSubmit()}
      >
        <span className="min-w-0 truncate">
          {p.waitingForCheck ? <Checking /> : p.submitting ? <Submitting /> : ready}
        </span>
        {!busy && !p.blocked && (
          <>
            <Arrow />
            <span className="inline-flex gap-1 max-[720px]:hidden pointer-coarse:hidden" aria-hidden="true">
              <kbd>Ctrl</kbd>
              <kbd>Enter</kbd>
            </span>
          </>
        )}
      </button>
    );
  } else if (s.status === "hashing") {
    primary = (
      <Button variant="cta" size="lg" aria-disabled onClick={() => undefined}>
        Hashing… <span className="num">{Math.round(s.progress * 100)}%</span>
      </Button>
    );
  } else {
    primary = (
      <Button variant="cta" size="lg" arrow onClick={p.onChoose}>
        {s.status === "idle" && !p.batch ? "Choose a sample…" : "Choose files…"}
      </Button>
    );
  }
  return (
    <div className="ctas">
      {primary}
      <Button variant="secondary" size="lg" icon="link" onClick={p.onPasteUrl}>
        Paste a URL
      </Button>
    </div>
  );
}

export function BatchLine({ batch, onRemove, onAdd, onClear }: { batch: Batch; onRemove: (f: File) => void; onAdd: () => void; onClear: () => void }) {
  const archives = batch.files.filter((f) => isArchiveName(f.name)).length;
  const notes = [
    `${batch.files.length}/${MAX_BULK} files`,
    batch.leftOut ? `${batch.leftOut} left out` : "",
    archives ? "archives unpack on the server" : "",
  ].filter(Boolean);
  const skipped = batch.skipped.map((x) => `${x.name}: ${x.reason === "empty" ? "empty" : "over 512 MiB"}`);
  return (
    <div className="batch">
      <ul aria-label="Files to submit">
        {batch.files.map((f) => (
          <li key={`${f.name}\0${f.size}\0${f.lastModified}`}>
            <b translate="no" title={f.name}>
              <MT value={f.name} tail={12} />
            </b>
            {isArchiveName(f.name) ? <MicroTag>archive</MicroTag> : <span />}
            <span className="tabular-nums">{bytes(f.size)}</span>
            <button type="button" className="batch-x" aria-label={`Remove ${f.name}`} title="Remove" onClick={() => onRemove(f)}>
              ×
            </button>
          </li>
        ))}
      </ul>
      <p className="batch-foot">
        <span>
          {notes.join(" · ")}
          {skipped.length > 0 && <span title={skipped.join("\n")}> · {skipped.length} skipped</span>}
        </span>
        <span className="inline-flex gap-3">
          {batch.files.length < MAX_BULK && (
            <button type="button" className="lnk" onClick={onAdd}>
              add
            </button>
          )}
          <button type="button" className="lnk" onClick={onClear}>
            clear
          </button>
        </span>
      </p>
    </div>
  );
}

export function PickedLine({ mode, pick, signIn, onChange }: { mode: "file" | "url"; pick: PickState; signIn?: boolean; onChange: () => void }) {
  if (signIn && pick.status === "idle") {
    return <p className="picked">sign-in required</p>;
  }
  if (signIn && mode === "file" && pick.status !== "idle") {
    // signing in is a full-page round trip: a picked file does not survive it
    return (
      <p className="picked">
        <b translate="no">
          <MT value={pick.file.name} tail={14} />
        </b>
        <span className="faint">·</span>
        <span>sign in, then choose the file again</span>
      </p>
    );
  }
  if (mode === "url") {
    return (
      <p className="picked">
        <Kbd>Esc</Kbd> back to file
      </p>
    );
  }
  if (pick.status === "idle") {
    return (
      <p className="picked">
        <span className="pointer-coarse:hidden">
          or drop a file on the plate · paste with <Kbd keys={["Ctrl", "V"]} />
        </span>
        <span className="hidden pointer-coarse:inline">or tap the plate to choose a file</span>
      </p>
    );
  }
  const change = (
    <button type="button" className="lnk" onClick={onChange}>
      change
    </button>
  );
  // a long name takes its own line (middle-truncated), so the facts line never starts with a separator
  const long = pick.file.name.length > 40;
  const name = (
    <>
      <b translate="no" className={long ? "basis-full text-center" : undefined}>
        <MT value={pick.file.name} tail={14} />
      </b>
      {!long && <span className="faint">·</span>}
    </>
  );
  if (pick.status === "picked") {
    return (
      <p className="picked">
        {name}
        <span>{pick.analysis.type}</span>
        <span className="faint">·</span>
        <span>{bytes(pick.file.size)}</span>
        <span className="faint">·</span>
        <Hash value={pick.analysis.sha256} short />
        <span className="faint">·</span>
        {change}
      </p>
    );
  }
  return (
    <p className="picked">
      {name}
      <span>{bytes(pick.file.size)}</span>
      {pick.status === "hashing" ? (
        <>
          <span className="faint">·</span>
          <span>hashing</span>
        </>
      ) : (
        <>
          <span className="faint">·</span>
          {change}
        </>
      )}
    </p>
  );
}

function SeenStatus({ job, count }: { job: Job; count: number }) {
  const now = useClock(30_000);
  const line = (status: Parameters<typeof RunMark>[0]["status"]) => (
    <p className="seen">
      <span>
        Seen before{count > 1 ? <span className="faint"> ×{count}</span> : null}
      </span>
      <span className="faint">·</span>
      <RunMark status={status} overdue={pastTimeout(job, now)} />
      <span className="faint">·</span>
      <time dateTime={job.submitted_at} title={`${job.submitted_at.replace("T", " ").replace("Z", "")} UTC`}>
        {ago(job.submitted_at, now)}
      </time>
      {/* on phones the link takes its own line, so no line starts with a separator */}
      <span className="faint max-[480px]:hidden">·</span>
      <Link className="action" to={`/jobs/${job.id}`} viewTransition>
        Open report
        <Arrow />
      </Link>
    </p>
  );
  return needsStream(job) ? <StreamedStatus job={job}>{line}</StreamedStatus> : line(recordStatus(job));
}

export function SeenLine({ seen }: { seen: { job: Job; count: number } | null }) {
  if (!seen) return null;
  return <SeenStatus job={seen.job} count={seen.count} />;
}
